'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneNumberForm } from './PhoneNumberForm';
import { OtpCodeForm } from './OtpCodeForm';
import { getAuthClient } from '@/integrations/auth-client';
import { useRouter, useSearchParams } from 'next/navigation';

type Step = 'phone' | 'otp';

interface OtpFormProps {
  origin: string;
  language?: 'en' | 'de';
  redirectUrl?: string;
}

export const OtpForm: React.FC<OtpFormProps> = (props) => {
  const { origin, language = 'en' } = props;
  const router = useRouter();
  const searchParams = useSearchParams();

  // Read query parameters
  const phoneParam = searchParams.get('phone');
  const otpParam = searchParams.get('otp');

  // Initialize state based on query parameters
  const [step, setStep] = useState<Step>(() => {
    // If both phone and otp params exist, go directly to otp step
    return phoneParam && otpParam ? 'otp' : 'phone';
  });
  const [phoneNumber, setPhoneNumber] = useState(phoneParam || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authClient = getAuthClient(origin);

  // Get redirect URL from search params or use language-specific default
  const redirectUrl =
    searchParams.get('next') || `/${language}/consent-manager`;

  const handleSendOtp = async (phoneNumberValue: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authClient.phoneNumber.sendOtp({
        phoneNumber: phoneNumberValue,
      });

      if (response.error) {
        setError(response.error.message || 'Failed to send OTP');
      } else {
        setPhoneNumber(phoneNumberValue);
        setStep('otp');
      }
    } catch (error: unknown) {
      console.error(error);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authClient.phoneNumber.verify({
        phoneNumber,
        code,
      });

      if (response.error) {
        setError(response.error.message || 'Invalid OTP code');
      } else {
        // Successful verification - redirect to specified URL
        router.push(redirectUrl);
      }
    } catch (error: unknown) {
      console.error(error);
      setError('Invalid OTP code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (phoneParam && otpParam && step === 'otp') {
      handleVerifyOtp(otpParam);
    }
  }, [phoneParam, otpParam, step]);

  const handleBackToPhone = () => {
    setStep('phone');
    setError(null);
  };

  const variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.2,
        stiffness: 100,
      },
    },
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4 text-white">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={variants}
        className="w-full max-w-md"
      >
        <div className="space-y-8 overflow-hidden rounded-2xl border border-white/20 bg-[#787878]/20 px-8 py-12 backdrop-blur-lg">
          {step === 'phone' ? (
            <PhoneNumberForm
              onSubmit={handleSendOtp}
              loading={loading}
              error={error}
              language={language}
            />
          ) : (
            <OtpCodeForm
              phoneNumber={phoneNumber}
              onSubmit={handleVerifyOtp}
              onBack={handleBackToPhone}
              loading={loading}
              error={error}
              language={language}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};
